import { createReducer, on } from '@ngrx/store';
import { closingDocumentActions } from '../actions/closing-document.actions';
import { projectAccountsActions } from '../actions/project-accounts.actions';
import {
    IAbcpSyncResult,
    IAccountStatePatch,
    IClassificationSummary,
    IComparisonPeriod,
    IJournalPeriod,
    IPeriodBounds,
    IProjectAccount,
} from '../models/project-account.model';
import { IStatementLayouts } from '../models/statement-layout.model';

export const PROJECT_ACCOUNTS_FEATURE_KEY = 'projectAccounts';

/** What a request is doing, so the screen can tell "empty" from "not loaded yet". */
export type RequestStatus = 'idle' | 'loading' | 'loaded' | 'failed';

export interface ProjectAccountsState {
    /**
     * The closing the state belongs to.
     *
     * Load-bearing rather than informational: a late answer for another closing is dropped against
     * this, and switching closings clears instead of showing the previous one's accounts.
     */
    readonly projectId: string | null;
    readonly accounts: readonly IProjectAccount[];
    readonly accountsStatus: RequestStatus;
    readonly accountsError: string | null;

    /**
     * The DEFAULT view of the list — prior year alone, zero accounts left out — whatever the screens
     * show in `accounts`.
     *
     * The navigation, the Jahresabschluss overview and the Kontenplan guard read this one. They used to
     * read `accounts`, which the Kontenplan, the «Nullkonten» chip and the comparison chips all change,
     * so a step's status followed whichever list was loaded last.
     */
    readonly stepAccounts: readonly IProjectAccount[];
    readonly stepAccountsStatus: RequestStatus;

    /** The closing's own period, as the last answer reported it. Labels the current column. */
    readonly period: IPeriodBounds | null;
    /**
     * The comparison periods the last ANSWER covered, with their status.
     *
     * Not the same as `selectedComparisonPeriods`: this is what came back, that is what was asked
     * for. They differ while a request is in flight, and they differ permanently for a period the
     * backend holds no journal for — which is exactly the case the screen has to report.
     */
    readonly comparisons: readonly IComparisonPeriod[];
    /** How the two statements are structured, as the last answer sent it. Null from an older backend. */
    readonly statementLayouts: IStatementLayouts | null;
    /** The closing's currency from its settings, as the last answer sent it. */
    readonly currency: string | null;
    /** What the user has chosen, in display order. Empty means "let the backend pick the prior year". */
    readonly selectedComparisonPeriods: readonly IPeriodBounds[];

    /** Whether accounts that are zero in every period are kept. Off by default, like the route. */
    readonly withZeroAccounts: boolean;

    /**
     * The workspace header's search term on the `Konten` tab (REV-22006). Applied in the CLIENT by the
     * presenters: the list is one closing's chart of accounts, already loaded, so a round trip per
     * keystroke would buy nothing — the same trade the correction register makes.
     */
    readonly searchTerm: string;

    /** Every period a journal exists for. The chips are built from this and from nothing else. */
    readonly journalPeriods: readonly IJournalPeriod[];
    readonly journalPeriodsStatus: RequestStatus;
    /** When this closing last pulled its ERP. Null when it never has. */
    readonly lastErpSyncAt: string | null;
    /**
     * The period this closing COVERS, from the closing itself.
     *
     * Kept apart from `period` above, which is the period an account list was actually READ for. Only
     * this one is answered before the first sync — and that is when the sync dialog needs it, to have
     * anything to step back from.
     */
    readonly closingPeriod: IPeriodBounds | null;

    readonly summary: IClassificationSummary | null;
    readonly summaryStatus: RequestStatus;
    /**
     * The classifier stopped answering: `oldestRequestedAt` is older than the polling window and the
     * halves have not moved.
     *
     * A FLAG and not a computed clock, because the polling effect already has to know the age to
     * decide whether to schedule another tick — the decision is made once, where the data is, and the
     * surface reads a boolean instead of comparing timestamps on every change detection.
     *
     * No scheduler on the backend notices a lost classification request; the reader computes
     * staleness. Without this the banner says "the AI is working" forever.
     */
    readonly classificationOverdue: boolean;

    /**
     * Whether a reconciliation poll chain (#162) is currently active for this closing — the
     * double-start guard (spec:GH-162 S1). A chain that has ENDED (ceiling, or nothing outstanding) is
     * not active, so re-entering the screen with an account still outstanding starts a new one.
     */
    readonly reconciliationPolling: boolean;
    /**
     * Whether the LAST reconciliation answer had something open — an account in
     * {@link reconciliationWatch} still reported as `reconciliation === 'NOT_ATTEMPTED'`. `null`
     * before the first answer. Compared PAIRWISE (`reloadAfterReconciliation$`) to trigger the one
     * safe list refetch, the same construction as `reloadAfterClassification$` uses for the
     * classification counters.
     *
     * ⚠️ ROUND 9 (contract-r8.md) — this used to be `evidenceComplete && reconciliation ===
     * 'NOT_ATTEMPTED'`. That pair is structurally unreachable on the running stack:
     * `EVIDENCE_RECONCILED` is the only writer of `evidenceComplete: true`, and it moves
     * `reconciliation` off `NOT_ATTEMPTED` in the SAME write — so the "filed, run still working"
     * state is byte-identical to the seed on the wire, and a poll gated on that pair ends after one
     * tick every time (Demo Lauf 12, 06.09.2026). `reconciliationWatch` replaces it.
     */
    readonly reconciliationOpen: boolean | null;
    /**
     * Account ids THIS SESSION expects a reconciliation run for (#162 round 9) — the accounts-side
     * twin of `pendingUploadFilenames` (`closing-document.reducer.ts:76-82`/`:263-273`). The counter
     * route cannot say "a run is under way" (see the note on {@link reconciliationOpen} above), so
     * this reads it off the WRITE that starts one instead: opened by the SUCCESS of `Assign Document`
     * / `Accept Proposal` (`pendingAssignmentDocumentIds` below carries the correlation), never by a
     * plain document read. Cleared PER ACCOUNT the instant a status answer stops reporting it as
     * `NOT_ATTEMPTED`, and wholesale by `forNewProject` — a window left open across a closing switch
     * would poll the account of a closing the trustee is no longer looking at (case 15).
     */
    readonly reconciliationWatch: readonly string[];
    /**
     * Correlation only — never read by a selector or an effect. DocumentIds for which `Assign
     * Document` or `Accept Proposal` was just dispatched, so the reducer can tell THEIR success
     * apart from the identical `loadDocumentSuccess` a plain sidebar open also fires. Removed the
     * moment that answer arrives, whether or not it names an account.
     *
     * ⚠️ Not airtight against re-opening the SAME document while its own write is still in flight
     * (whichever `loadDocumentSuccess` for it lands first consumes the marker) — a named, accepted
     * gap rather than a silent one: neither gesture this exists for is reachable from a plain re-open
     * in the running product.
     */
    readonly pendingAssignmentDocumentIds: readonly string[];

    readonly syncStatus: RequestStatus;
    readonly syncError: string | null;
    /**
     * What the last pull did, per comparison period.
     *
     * Kept rather than discarded: `FILLED`, `ALREADY_PRESENT` and `FAILED` are three different
     * answers to "why did nothing change", and a spinner that simply stops tells the trustee none of
     * them.
     */
    readonly syncResult: IAbcpSyncResult | null;

    readonly bindingStatus: RequestStatus;
    readonly bindingError: string | null;

    /**
     * REV-22005 — the trustee's last account-state write (review flag, document value). ONE status for
     * the three kinds: a person edits one account at a time, and the dialog that is open is the one
     * the answer belongs to. `accountWriteError` is the backend's stable message key.
     */
    readonly accountWriteStatus: RequestStatus;
    readonly accountWriteError: string | null;
}

export const initialProjectAccountsState: ProjectAccountsState = {
    projectId: null,
    accounts: [],
    accountsStatus: 'idle',
    accountsError: null,
    stepAccounts: [],
    stepAccountsStatus: 'idle',
    period: null,
    comparisons: [],
    statementLayouts: null,
    currency: null,
    selectedComparisonPeriods: [],
    withZeroAccounts: false,
    searchTerm: '',
    journalPeriods: [],
    journalPeriodsStatus: 'idle',
    lastErpSyncAt: null,
    closingPeriod: null,
    summary: null,
    summaryStatus: 'idle',
    classificationOverdue: false,
    reconciliationPolling: false,
    reconciliationOpen: null,
    reconciliationWatch: [],
    pendingAssignmentDocumentIds: [],
    syncStatus: 'idle',
    syncError: null,
    syncResult: null,
    bindingStatus: 'idle',
    bindingError: null,
    accountWriteStatus: 'idle',
    accountWriteError: null,
};

/** Is this answer for the closing the state is about? A late one for another is ignored. */
function isCurrent(state: ProjectAccountsState, projectId: string): boolean {
    return state.projectId === projectId;
}

/**
 * Everything that belongs to ONE closing, cleared when the state moves to another.
 *
 * One helper because EVERY action that sets `projectId` has to do this. It used to live inline in
 * `loadAccounts` alone, so opening a second closing through any other path — the journals call the
 * header makes on every tab, the counters, a sync — carried the previous closing's state across. The
 * visible symptom was the last pull's summary modal reappearing on the next closing you opened.
 *
 * Written as a spread so a reducer can apply it conditionally and still set its own fields after.
 */
function forNewProject(state: ProjectAccountsState, projectId: string): Partial<ProjectAccountsState> {
    if (isCurrent(state, projectId)) {
        return {};
    }

    return {
        accounts: [],
        // The reconciliation poll's entry tick (`loadReconciliationStatus`, tick === 1) reaches this
        // BEFORE `loadAccounts` does on a genuine cold entry — the counter route is fulfilled from an
        // effect, the list is a real request — so a reader that decides "has THIS closing's list
        // loaded" off `accountsStatus` needs it to already read as unsettled the instant the closing
        // changes, not still carry the PREVIOUS closing's `'loaded'`.
        accountsStatus: 'idle' as const,
        accountsError: null,
        stepAccounts: [],
        stepAccountsStatus: 'idle' as const,
        summary: null,
        summaryStatus: 'idle',
        classificationOverdue: false,
        // A new closing starts with no chain and no known openness — carrying either over would let a
        // stale flag from the closing just left block (or wrongly permit) the first poll of this one.
        reconciliationPolling: false,
        reconciliationOpen: null,
        // #162 round 9 — a watch (or a write still correlating) belongs to the closing it was opened
        // on. Carrying it over would poll — or misattribute an assignment's response to — an account
        // of the closing the trustee just left (case 15).
        reconciliationWatch: [],
        pendingAssignmentDocumentIds: [],
        // A different closing has different periods. Keeping the old ones would offer chips for a
        // company that may not even be the same one.
        period: null,
        comparisons: [],
        // Belongs to the answer it came with, like the periods.
        statementLayouts: null,
        currency: null,
        // The zero-account view is a choice on one closing; the next one starts with the default list.
        withZeroAccounts: false,
        selectedComparisonPeriods: [],
        journalPeriods: [],
        journalPeriodsStatus: 'idle',
        lastErpSyncAt: null,
        closingPeriod: null,
        // The pull belongs to the closing it ran for. Carried over, its summary opens on a closing that
        // was never synced and reports another one's figures.
        syncStatus: 'idle',
        syncError: null,
        syncResult: null,
        // REV-22005 — a write answer belongs to the closing it was made on.
        accountWriteStatus: 'idle',
        accountWriteError: null,
    };
}

/** Whether the shared list is the backend's default view: no zero accounts, no chosen comparison periods. */
function isDefaultView(state: ProjectAccountsState): boolean {
    return !state.withZeroAccounts && state.selectedComparisonPeriods.length === 0;
}

/**
 * REV-22005 — the server's answer folded onto the ONE row it describes. Every other row keeps its
 * reference, so an OnPush table re-renders one line, not the list. A row the list does not hold (a
 * zero account filtered out) is left alone rather than invented.
 */
function withPatchedRow(accounts: readonly IProjectAccount[], patch: IAccountStatePatch): readonly IProjectAccount[] {
    return accounts.map((account) =>
        account.accountId === patch.accountId
            ? {
                  ...account,
                  reconciliation: patch.reconciliation,
                  evidenceComplete: patch.evidenceComplete,
                  documentBalance: patch.documentBalance,
                  documentBalanceOrigin: patch.documentBalanceOrigin,
                  flag: patch.flag,
              }
            : account,
    );
}

/**
 * Add `id` to `ids`, deduplicated — the same helper as `closing-document.reducer.ts`'s of the same
 * name, kept file-local rather than shared: both are a handful of lines, and importing one across
 * this particular boundary would be the tail wagging the dog.
 */
function withId(ids: readonly string[], id: string): readonly string[] {
    return ids.includes(id) ? ids : [...ids, id];
}

/** Remove `id` from `ids`, or return the SAME array reference if it was never in it. */
function withoutId(ids: readonly string[], id: string): readonly string[] {
    return ids.includes(id) ? ids.filter((existing) => existing !== id) : ids;
}

export const projectAccountsReducer = createReducer(
    initialProjectAccountsState,
    on(projectAccountsActions.loadAccounts, (state, { projectId }) => ({
        ...state,
        // A different closing starts from nothing. Keeping the old rows would show one closing's
        // accounts under another's heading for as long as the request takes.
        ...forNewProject(state, projectId),
        projectId,
        accountsStatus: 'loading' as const,
        accountsError: null,
    })),
    on(projectAccountsActions.loadAccountsSuccess, (state, { projectId, response }) =>
        isCurrent(state, projectId)
            ? {
                  ...state,
                  accounts: response.accounts,
                  // Taken from the ANSWER, not from what was asked for. A column may then be labelled
                  // only with a period the backend actually read.
                  period: response.period,
                  comparisons: response.comparisons,
                  statementLayouts: response.statementLayouts ?? null,
                  currency: response.currency ?? null,
                  accountsStatus: 'loaded' as const,
                  accountsError: null,
                  // The shared list IS the default view when nothing is switched on or chosen — the
                  // same request, so it is taken over instead of asked for a second time. Both setters
                  // refetch through their own effects, so the answer here belongs to this very state.
                  ...(isDefaultView(state)
                      ? { stepAccounts: response.accounts, stepAccountsStatus: 'loaded' as const }
                      : {}),
              }
            : state,
    ),
    on(projectAccountsActions.loadAccountsFailure, (state, { projectId, error }) =>
        isCurrent(state, projectId) ? { ...state, accountsStatus: 'failed' as const, accountsError: error } : state,
    ),

    on(projectAccountsActions.loadStepAccounts, (state, { projectId }) => ({
        ...state,
        ...forNewProject(state, projectId),
        projectId,
        stepAccountsStatus: 'loading' as const,
    })),
    on(projectAccountsActions.loadStepAccountsSuccess, (state, { projectId, accounts }) =>
        isCurrent(state, projectId)
            ? { ...state, stepAccounts: accounts, stepAccountsStatus: 'loaded' as const }
            : state,
    ),
    on(projectAccountsActions.loadStepAccountsFailure, (state, { projectId }) =>
        isCurrent(state, projectId) ? { ...state, stepAccountsStatus: 'failed' as const } : state,
    ),

    on(projectAccountsActions.loadJournalPeriods, (state, { projectId }) => ({
        ...state,
        ...forNewProject(state, projectId),
        projectId,
        journalPeriodsStatus: 'loading' as const,
    })),
    on(projectAccountsActions.loadJournalPeriodsSuccess, (state, { projectId, state: synced }) =>
        isCurrent(state, projectId)
            ? {
                  ...state,
                  journalPeriods: synced.periods,
                  lastErpSyncAt: synced.lastErpSyncAt,
                  closingPeriod: synced.closingPeriod,
                  journalPeriodsStatus: 'loaded' as const,
              }
            : state,
    ),
    on(projectAccountsActions.loadJournalPeriodsFailure, (state, { projectId }) =>
        // No error field: without the list the chips simply do not appear. There is nothing for the
        // user to do about it, and a banner over a working table would only be noise.
        isCurrent(state, projectId) ? { ...state, journalPeriodsStatus: 'failed' as const } : state,
    ),

    on(projectAccountsActions.selectComparisonPeriods, (state, { projectId, comparisonPeriods }) =>
        isCurrent(state, projectId) ? { ...state, selectedComparisonPeriods: comparisonPeriods } : state,
    ),

    on(projectAccountsActions.setWithZeroAccounts, (state, { projectId, withZeroAccounts }) =>
        isCurrent(state, projectId) ? { ...state, withZeroAccounts } : state,
    ),

    // Not guarded by the closing: the term belongs to the SCREEN, like the header field it mirrors.
    on(projectAccountsActions.setSearchTerm, (state, { searchTerm }) => ({ ...state, searchTerm })),

    /**
     * The polling chain gave up. Guarded like every other answer: a verdict about the closing the
     * user has since left must not land on the one they are looking at.
     */
    on(projectAccountsActions.classificationOverdue, (state, { projectId }) =>
        isCurrent(state, projectId) ? { ...state, classificationOverdue: true } : state,
    ),
    on(projectAccountsActions.loadSummary, (state, { projectId }) => ({
        ...state,
        ...forNewProject(state, projectId),
        projectId,
        summaryStatus: 'loading' as const,
    })),
    on(projectAccountsActions.loadSummarySuccess, (state, { projectId, summary }) =>
        isCurrent(state, projectId) ? { ...state, summary, summaryStatus: 'loaded' as const } : state,
    ),
    on(projectAccountsActions.loadSummaryFailure, (state, { projectId }) =>
        isCurrent(state, projectId) ? { ...state, summaryStatus: 'failed' as const } : state,
    ),

    /**
     * A reconciliation poll tick was DISPATCHED — sets the double-start guard.
     *
     * ⚠️ ROUND 4 (SRR F2) — `forNewProject` is applied ONLY on `tick === 1`, the genuine entry dispatch
     * from `startReconciliationPoll$`. A LATER tick (2+) is this chain's OWN self-scheduled continuation
     * for the closing it started on; if the user has since moved to another closing, `projectId` here is
     * the OLD one and `state.projectId` is already the NEW one. Applying `forNewProject` for that case
     * used to reset the NEW closing's slice back to the OLD `projectId` on every stray tick — the old
     * chain never landed anything (the effects below already guarded that), but the reducer let it keep
     * BEING ASKED by resurrecting itself as "current" each time it ticked. A stray tick is now a no-op
     * unless it still is current, so nothing here restarts the chain the effects have already moved on
     * from.
     */
    on(projectAccountsActions.loadReconciliationStatus, (state, { projectId, tick }) =>
        tick === 1
            ? { ...state, ...forNewProject(state, projectId), projectId, reconciliationPolling: true }
            : isCurrent(state, projectId)
            ? { ...state, reconciliationPolling: true }
            : state,
    ),
    on(projectAccountsActions.loadReconciliationStatusSuccess, (state, { projectId, status }) =>
        isCurrent(state, projectId)
            ? {
                  ...state,
                  // #162 round 9 — "open" now means: an account THIS SESSION IS WATCHING is still
                  // reported `NOT_ATTEMPTED`. Read off the WATCH AS IT STOOD BEFORE THIS ANSWER (the
                  // pruned value below is computed from the very same pair and applied alongside, not
                  // chained) — the same instant the pruning line judges settled.
                  reconciliationOpen: status.accounts.some(
                      (account) =>
                          state.reconciliationWatch.includes(account.accountId) &&
                          account.reconciliation === 'NOT_ATTEMPTED',
                  ),
                  // The window closes PER ACCOUNT the moment its own answer stops reporting
                  // `NOT_ATTEMPTED` — a settled run has nothing left to watch for, and leaving it in
                  // would let an unrelated later event (e.g. an ERP re-sync resetting every account
                  // back to `NOT_ATTEMPTED`, contract-r8.md (1)) reopen a poll for an account whose own
                  // run this session already saw answered. An account this answer does not mention at
                  // all is left in the window rather than dropped — silence is not settlement.
                  reconciliationWatch: state.reconciliationWatch.filter((accountId) => {
                      const reported = status.accounts.find((account) => account.accountId === accountId);
                      return reported === undefined || reported.reconciliation === 'NOT_ATTEMPTED';
                  }),
              }
            : state,
    ),
    // No handler for `Load Reconciliation Status Failure`: a failed tick says nothing about which
    // accounts are open, so `reconciliationOpen` (and the watch) are left exactly as the last real
    // answer left them (spec:GH-162 S5) — the row keeps telling the truth it has.
    on(projectAccountsActions.reconciliationPollEnded, (state, { projectId }) =>
        isCurrent(state, projectId) ? { ...state, reconciliationPolling: false } : state,
    ),

    /**
     * #162 round 9 — the two write gestures that MAY open a run window (contract-r8.md (4)).
     * Neither sets {@link ProjectAccountsState.reconciliationWatch} directly: both answer through the
     * SAME `loadDocumentSuccess` a plain sidebar open also fires, so what is tracked here is only the
     * correlation — "the next success naming THIS documentId is this write landing, not a read".
     * `Reject Proposal` / `Unassign Document` are deliberately NOT listed: they take a document AWAY
     * from an account rather than starting a run on one.
     */
    on(closingDocumentActions.assignDocument, (state, { documentId }) => ({
        ...state,
        pendingAssignmentDocumentIds: withId(state.pendingAssignmentDocumentIds, documentId),
    })),
    on(closingDocumentActions.acceptProposal, (state, { documentId }) => ({
        ...state,
        pendingAssignmentDocumentIds: withId(state.pendingAssignmentDocumentIds, documentId),
    })),
    /**
     * The correlated success. A `loadDocumentSuccess` for a documentId NOT in
     * `pendingAssignmentDocumentIds` is a plain read (a sidebar opened, a deep link followed) and
     * changes nothing here — this is the fix for the case the round 8 contract's ⚠️ names. One that
     * IS tracked is either write's own answer: the marker is consumed either way, and the account is
     * added to the watch only when the document actually names one (`kind: 'projectFile'` — filed to
     * the Projekt-Akte, not an account — answers with `account: null` and opens nothing).
     */
    // REV-22003 — the AI's own assignment opens the window too (see the action). Guarded by the
    // closing like every answer: a late one for a closing the trustee has left watches nothing.
    on(projectAccountsActions.watchReconciliation, (state, { projectId, accountIds }) =>
        isCurrent(state, projectId)
            ? { ...state, reconciliationWatch: accountIds.reduce(withId, state.reconciliationWatch) }
            : state,
    ),
    on(closingDocumentActions.loadDocumentSuccess, (state, { document }) =>
        state.pendingAssignmentDocumentIds.includes(document.id)
            ? {
                  ...state,
                  pendingAssignmentDocumentIds: withoutId(state.pendingAssignmentDocumentIds, document.id),
                  reconciliationWatch:
                      document.account === null
                          ? state.reconciliationWatch
                          : withId(state.reconciliationWatch, document.account.id),
              }
            : state,
    ),
    /**
     * #162 round 10 (verify-r9-abort.md (4).1, case 25) — the write's OWN failure, missing its
     * success's twin above: `assignDocument$`'s `catchError` answers `loadDocumentsFailure()`, which
     * confirmed (`closing-document.actions.ts:53`) carries no `documentId` — `emptyProps`, unlike
     * `Proposal Failure` — so the marker cannot be removed by id as `loadDocumentSuccess` does above;
     * cleared wholesale instead. Before this handler the marker for a failed write survived for the
     * rest of the session, and the NEXT plain read of ANY document — not necessarily the same one —
     * landed on `loadDocumentSuccess` and was misread as that write's own success.
     */
    on(closingDocumentActions.loadDocumentsFailure, (state) =>
        state.pendingAssignmentDocumentIds.length === 0 ? state : { ...state, pendingAssignmentDocumentIds: [] },
    ),

    on(projectAccountsActions.writeBindings, (state) => ({
        ...state,
        bindingStatus: 'loading' as const,
        bindingError: null,
    })),
    on(projectAccountsActions.writeBindingsSuccess, (state) => ({
        ...state,
        bindingStatus: 'loaded' as const,
        bindingError: null,
    })),
    on(projectAccountsActions.writeBindingsFailure, (state, { error }) => ({
        ...state,
        bindingStatus: 'failed' as const,
        bindingError: error,
    })),

    // REV-22005 — a trustee's change to one account's state. Guarded by the closing like every answer.
    on(projectAccountsActions.writeAccountState, (state, { projectId }) =>
        isCurrent(state, projectId)
            ? { ...state, accountWriteStatus: 'loading' as const, accountWriteError: null }
            : state,
    ),
    on(projectAccountsActions.writeAccountStateSuccess, (state, { projectId, patch }) =>
        isCurrent(state, projectId)
            ? {
                  ...state,
                  accounts: withPatchedRow(state.accounts, patch),
                  stepAccounts: withPatchedRow(state.stepAccounts, patch),
                  accountWriteStatus: 'loaded' as const,
                  accountWriteError: null,
              }
            : state,
    ),
    on(projectAccountsActions.writeAccountStateFailure, (state, { projectId, error }) =>
        isCurrent(state, projectId)
            ? { ...state, accountWriteStatus: 'failed' as const, accountWriteError: error }
            : state,
    ),
    on(projectAccountsActions.resetAccountStateWrite, (state) => ({
        ...state,
        accountWriteStatus: 'idle' as const,
        accountWriteError: null,
    })),

    // REV-22007 — the file re-import runs through the same three fields: one progress modal, one summary.
    on(projectAccountsActions.sync, projectAccountsActions.syncFromFile, (state, { projectId }) => ({
        ...state,
        ...forNewProject(state, projectId),
        projectId,
        syncStatus: 'loading' as const,
        syncError: null,
        syncResult: null,
    })),
    on(projectAccountsActions.syncSuccess, (state, { result }) => ({
        ...state,
        syncStatus: 'loaded' as const,
        syncError: null,
        syncResult: result,
    })),
    on(projectAccountsActions.syncFailure, (state, { error }) => ({
        ...state,
        syncStatus: 'failed' as const,
        syncError: error,
    })),
    // A running pull keeps its outcome: closing a dialog must not hide the answer that is still coming.
    on(projectAccountsActions.resetSync, (state) =>
        state.syncStatus === 'loading'
            ? state
            : { ...state, syncStatus: 'idle' as const, syncError: null, syncResult: null },
    ),
);
